import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, RotateCcw, Trophy } from "lucide-react";
import { useQuizStore } from "@/data/quizStore";
import { useGamificationStore } from "@/data/gamificationStore";
import type { Lang } from "@/i18n/translations";
import SEO from "@/components/SEO";
import { motion } from "framer-motion";



const t_quiz: Record<Lang, Record<string, string>> = {
  fr: {
    title: "Quiz Politique",
    subtitle: "Découvrez quel parti correspond le mieux à vos valeurs",
    start: "Commencer le quiz",
    next: "Suivant",
    prev: "Précédent",
    seeResults: "Voir les résultats",
    restart: "Recommencer",
    resultTitle: "Votre Boussole!",
    resultSubtitle: "Étonné par le résultat ? C’est parfois là que la réflexion commence !",
    compatibility: "Compatibilité",
    question: "Question",
    of: "sur",
    selectAnswer: "Sélectionnez une réponse pour continuer",
    back: "Retour à l'accueil",
    viewParty: "Voir le parti",
    disclaimer: "Ces résultats sont basés sur vos réponses d'aujourd'hui. En politique comme dans la vie, les idées évoluent et c'est ce qui fait la richesse du débat !",
  },
  ar: {
    title: "اختبار سياسي",
    subtitle: "اكتشف أي حزب يتوافق أكثر مع قيمك",
    start: "ابدأ الاختبار",
    next: "التالي",
    prev: "السابق",
    seeResults: "عرض النتائج",
    restart: "إعادة",
    resultTitle: "بوصلتك!",
    resultSubtitle: "متفاجئ بالنتيجة؟ أحياناً من هنا يبدأ التفكير!",
    compatibility: "التوافق",
    question: "سؤال",
    of: "من",
    selectAnswer: "اختر إجابة للمتابعة",
    back: "العودة للرئيسية",
    viewParty: "عرض الحزب",
    disclaimer: "تستند هذه النتائج إلى إجاباتك اليوم. في السياسة كما في الحياة، تتطور الأفكار وهذا ما يثري النقاش!",
  },
  zgh: {
    title: "ⴰⵙⵉⵜⴳ ⴰⵙⵔⵜⴰⵏ",
    subtitle: "ⴰⴼ ⴰⵎⵓⵙⵙⵓ ⴰⵙⵔⵜⴰⵏ ⴰⴷ ⵉⵎⵙⴰⵙⴰⵏ ⴷ ⵡⴰⵜⵉⴳⵏ ⵏⵏⴽ",
    start: "ⵙⵏⵜⵉ ⴰⵙⵉⵜⴳ",
    next: "ⴰⴷⴷⴰⵢ",
    prev: "ⴰⵎⵣⵡⴰⵔⵓ",
    seeResults: "ⵥⵕ ⵜⵉⵢⴰⴼⵓⵜⵉⵏ",
    restart: "ⴰⵍⵙ",
    resultTitle: "ⵜⴰⵎⵏⵀⴰⴹⵜ ⵏⵏⴽ!",
    resultSubtitle: "ⵜⴻⵜⵜⵓⵡⴰⵀⵎⴷ ⵙ ⵜⵢⴰⴼⵓⵜ? ⴽⵔⴰ ⵏ ⵜⵉⴽⴽⴰⵍ ⴷⴰ ⴰⴳ ⵜⴱⴷⴷⴰ ⵜⵡⵏⴳⵉⵎⵜ!",
    compatibility: "ⴰⵎⵙⴰⵙⴰ",
    question: "ⴰⵙⵇⵙⵉ",
    of: "ⵙⴳ",
    selectAnswer: "ⵙⵜⵉ ⵢⴰⵜ ⵜⵎⵔⴰⵔⵓⵜ",
    back: "ⵓⵖⴰⵍ ⵙ ⵜⴰⵙⵏⴰ ⵜⴰⵎⵣⵡⴰⵔⵓⵜ",
    viewParty: "ⵥⵕ ⴰⵎⵓⵙⵙⵓ",
    disclaimer: "ⵜⵉⵢⴰⴼⵓⵜⵉⵏ ⴰⴷ ⴱⵉⴷⴷⵏⵜ ⵅⴼ ⵜⵎⵔⴰⵔⵓⵜⵉⵏ ⵏⵏⴽ ⵏ ⴰⵙⵙⴰ. ⴳ ⵜⵙⵔⵜⵉⵜ ⴰⵎ ⵜⵓⴷⵔⵜ, ⵜⵉⵡⵏⴳⵉⵎⵉⵏ ⴰⵔ ⵜⵜⵏⴼⵍⴰⵍⵏⵜ, ⴷ ⴰⵢⴰ ⴰⴷ ⵉⵙⵙⴱⵖⵓⵔⵏ ⴰⵎⴹⴰⴽⴰⵕ!",
  },
};

const allPartiesNames: Record<Lang, Record<string, string>> = {
  fr: {
    RNI: "Rassemblement National des Indépendants", PAM: "Parti Authenticité et Modernité",
    PI: "Parti de l'Istiqlal", USFP: "Union Socialiste des Forces Populaires",
    MP: "Mouvement Populaire", PPS: "Parti du Progrès et du Socialisme",
    UC: "Union Constitutionnelle", PJD: "Parti de la Justice et du Développement",
    PSU: "Parti Socialiste Unifié", PEDD: "Parti de l'Environnement et du Développement Durable",
    CNI: "Congrès National Ittihadi", PRV: "Parti de la Renaissance et de la Vertu",
    PT: "Parti Travailliste", PML: "Parti Marocain Libéral", PGVM: "Parti de la Gauche Verte Marocaine",
    PDI: "Parti Démocratique et de l'Indépendance", PLJS: "Parti de la Liberté et de la Justice Sociale",
    PCS: "Parti du Centre Social", PND: "Parti National Démocrate", "Al Ahd": "Parti Al Ahd Addimocrati",
    PRD: "Parti de la Réforme et du Développement",
  },
  ar: {
    RNI: "التجمع الوطني للأحرار", PAM: "حزب الأصالة والمعاصرة",
    PI: "حزب الاستقلال", USFP: "الاتحاد الاشتراكي للقوات الشعبية",
    MP: "الحركة الشعبية", PPS: "حزب التقدم والاشتراكية",
    UC: "الاتحاد الدستوري", PJD: "حزب العدالة والتنمية",
    PSU: "الحزب الاشتراكي الموحد", PEDD: "حزب البيئة والتنمية المستدامة",
    CNI: "المؤتمر الوطني الاتحادي", PRV: "حزب النهضة والفضيلة",
    PT: "حزب العمال", PML: "الحزب المغربي الليبرالي", PGVM: "حزب اليسار الأخضر المغربي",
    PDI: "حزب الديمقراطية والاستقلال", PLJS: "حزب الحرية والعدالة الاجتماعية",
    PCS: "حزب الوسط الاجتماعي", PND: "الحزب الوطني الديمقراطي", "Al Ahd": "حزب العهد الديمقراطي",
    PRD: "حزب الإصلاح والتنمية",
  },
  zgh: {
    RNI: "ⴰⵎⵓⵏ ⴰⵏⴰⵎⵓⵔ ⵏ ⵉⵍⴻⵍⵍⵉⵜⵏ", PAM: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵜⵉⵣⵉ ⴷ ⵜⴰⵜⵔⴰⵔⵜ",
    PI: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵓⵙⵉⵎⴰⵏ", USFP: "ⴰⵎⵓⵏ ⴰⵏⴰⵎⵓⵏ ⵏ ⵜⵖⵔⵎⵉⵡⵉⵏ ⵜⵉⵏⴰⵎⵓⵔⵉⵏ",
    MP: "ⵜⵉⵎⵓⵙⵙⵉ ⵜⴰⵏⴰⵎⵓⵔⵜ", PPS: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵓⴳⴳⵓⵣ ⴷ ⵜⵏⴰⵎⵓⵏⵜ",
    UC: "ⴰⵎⵓⵏ ⴰⴷⵓⵙⵜⵓⵔⴰⵏ", PJD: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵜⵣⵔⴼⵜ ⴷ ⵜⴳⵎⵉ",
    PSU: "ⴰⵎⵓⵙⵙⵓ ⴰⵏⴰⵎⵓⵏ ⴰⵎⵓⵏ", PEDD: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵓⵡⵏⴰⴹ",
    CNI: "ⴰⴳⵔⴰⵡ ⴰⵏⴰⵎⵓⵔ ⴰⵎⵓⵏⴰⵏ", PRV: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵜⴰⵍⵍⵉⵜ ⴷ ⵜⵥⵍⴰⵢⵜ",
    PT: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵉⵎⵙⵡⵓⵔⵉⵏ", PML: "ⴰⵎⵓⵙⵙⵓ ⴰⵎⵖⵔⵉⴱⵉ ⴰⵍⵉⴱⵉⵔⴰⵍ",
    PGVM: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵓⵣⵍⵎⴰⴹ ⴰⵣⴳⵣⴰⵡ", PDI: "ⴰⵎⵓⵙⵙⵓ ⴰⴷⵉⵎⵓⵇⵔⴰⵟⵉ",
    PLJS: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵜⵉⵍⴻⵍⵍⵉ ⴷ ⵜⵣⵔⴼⵜ", PCS: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵡⴰⵎⵎⴰⵙ",
    PND: "ⴰⵎⵓⵙⵙⵓ ⴰⵏⴰⵎⵓⵔ ⴰⴷⵉⵎⵓⵇⵔⴰⵟⵉ", "Al Ahd": "ⴰⵎⵓⵙⵙⵓ ⵏ ⵓⵄⵀⴷ ⴰⴷⵉⵎⵓⵇⵔⴰⵟⵉ",
    PRD: "ⴰⵎⵓⵙⵙⵓ ⵏ ⵓⵙⵏⴼⵍ ⴷ ⵜⴳⵎⵉ",
  },
};

const partyColors: Record<string, string> = {
  RNI: "hsl(220 60% 50%)", PAM: "hsl(30 80% 50%)", PI: "hsl(150 60% 35%)",
  USFP: "hsl(350 70% 50%)", MP: "hsl(100 50% 40%)", PPS: "hsl(0 80% 45%)",
  UC: "hsl(210 70% 45%)", PJD: "hsl(45 70% 45%)", PSU: "hsl(350 55% 45%)",
  PEDD: "hsl(120 55% 40%)", CNI: "hsl(200 55% 45%)", PRV: "hsl(140 45% 45%)",
  PT: "hsl(5 65% 50%)", PML: "hsl(270 45% 50%)", PGVM: "hsl(160 50% 45%)",
  PDI: "hsl(260 45% 50%)", PLJS: "hsl(40 55% 50%)", PCS: "hsl(300 40% 50%)",
  PND: "hsl(230 50% 50%)", "Al Ahd": "hsl(80 50% 40%)", PRD: "hsl(170 50% 40%)",
};

const QuizPage = () => {
  const { lang, isRtl } = useI18n();
  const qt = t_quiz[lang];

  const { questions, recordPlay, itemsPerSession } = useQuizStore();
  const { addXp, unlockBadge } = useGamificationStore();
  const allValidQuestions = questions.filter(q => q.status === 'valide');
  
  const [sessionQuestions, setSessionQuestions] = useState<any[]>([]);

  useEffect(() => {
    initSession();
  }, [questions, itemsPerSession]);

  const initSession = (previousSession: any[] = []) => {
    if (allValidQuestions.length <= itemsPerSession) {
      setSessionQuestions([...allValidQuestions].sort(() => 0.5 - Math.random()));
      return;
    }
    const retainCount = Math.min(previousSession.length, Math.floor(Math.random() * 2) + 2);
    const retained = previousSession.slice(0, retainCount);
    const remainingProps = allValidQuestions.filter(p => !retained.find(r => r.id === p.id));
    const shuffled = [...remainingProps].sort(() => 0.5 - Math.random());
    const newItems = shuffled.slice(0, itemsPerSession - retainCount);
    setSessionQuestions([...retained, ...newItems].sort(() => 0.5 - Math.random()));
  };

  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Initialize answers array when questions are loaded
  if (answers.length !== sessionQuestions.length && sessionQuestions.length > 0) {
    setAnswers(Array(sessionQuestions.length).fill(null));
  }

  const handleAnswer = (optionIndex: string) => {
    const newAnswers = [...answers];
    newAnswers[current] = parseInt(optionIndex);
    setAnswers(newAnswers);
  };

  const calculateResults = () => {
    const scores: Record<string, number> = {};
    answers.forEach((ansIdx, qIdx) => {
      if (ansIdx !== null) {
        const option = sessionQuestions[qIdx].options[ansIdx];
        Object.entries(option.scores).forEach(([party, score]) => {
          scores[party] = (scores[party] || 0) + (score as number);
        });
      }
    });

    const maxScore = Math.max(...Object.values(scores), 1);
    return Object.entries(scores)
      .map(([party, score]) => ({ party, score, percent: Math.round((score / maxScore) * 100) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  };

  const restart = () => {
    setStarted(false);
    setCurrent(0);
    setAnswers(Array(sessionQuestions.length).fill(null));
    setShowResults(false);
    initSession(sessionQuestions);
  };

  const progress = sessionQuestions.length > 0 ? ((current + 1) / sessionQuestions.length) * 100 : 0;

  if (!started) {
    return (
      <div className="min-h-screen bg-background" dir={isRtl ? "rtl" : "ltr"}>
        <SEO 
          title="Kayn'in | Boussole Électorale 2026"
          description="Testez notre Boussole Électorale pour découvrir le parti politique qui correspond le mieux à vos valeurs."
          url="https://kaynin.ma/quiz"
        />
        <Navbar />
        <div className="pt-24 pb-16 container max-w-2xl">
          <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/accueil-quiz">
              <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />
              {qt.back}
            </Link>
          </Button>
          <Card className="text-center border-2 border-primary/20 shadow-[var(--shadow-elevated)]">
            <CardHeader className="pb-4">
              <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Trophy className="w-10 h-10 text-primary" />
              </div>
              <CardTitle className="text-3xl font-display font-extrabold text-foreground">{qt.title}</CardTitle>
              <p className="text-muted-foreground mt-2 text-lg">{qt.subtitle}</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm font-medium text-muted-foreground italic p-3 bg-muted/30 rounded-lg">{qt.disclaimer}</p>
              <Button size="lg" className="text-lg px-10" onClick={() => setStarted(true)}>
                {qt.start}
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  if (showResults) {
    const results = calculateResults();
    // Enregistrer la participation si pas encore fait
    // Utiliser un appel unique pour ne pas créer de boucle de rendu
    // C'est géré au clic sur "Voir les résultats"

    return (
      <div className="min-h-screen bg-background" dir={isRtl ? "rtl" : "ltr"}>
        <SEO 
          title="Kayn'in | Résultats de la Boussole Électorale"
          description={`Découvrez votre compatibilité politique pour les élections 2026. Top résultat: ${results[0]?.party || ''}.`}
          url="https://kaynin.ma/quiz"
        />
        <Navbar />
        <div className="pt-24 pb-16 container max-w-2xl">
          <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/accueil-quiz">
              <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />
              {qt.back}
            </Link>
          </Button>
          <Card className="border-2 border-primary/20 shadow-[var(--shadow-elevated)]">
            <CardHeader className="text-center pb-2">
              <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 0.8, ease: "easeInOut" }}
                className="mx-auto mb-4 w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center"
              >
                <Trophy className="w-8 h-8 text-accent" />
              </motion.div>
              <CardTitle className="text-2xl font-display font-extrabold text-foreground">{qt.resultTitle}</CardTitle>
              <p className="text-muted-foreground">{qt.resultSubtitle}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {results.map((r, i) => (
                <div key={r.party} className="p-4 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: partyColors[r.party] || "hsl(var(--primary))" }}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <span className="font-bold text-foreground">{r.party}</span>
                        <p className="text-xs text-muted-foreground">{allPartiesNames[lang][r.party] || r.party}</p>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-primary">{r.percent}%</span>
                  </div>
                  <Progress value={r.percent} className="h-2" />
                  <div className="mt-2 text-end">
                    <Link to={`/parti/${r.party}`} className="text-xs text-primary hover:underline">
                      {qt.viewParty} →
                    </Link>
                  </div>
                </div>
              ))}

              <p className="text-sm font-medium text-foreground/80 italic text-center p-4 bg-muted/30 rounded-lg mt-6 shadow-sm border border-border/50">
                {qt.disclaimer}
              </p>

              <div className="flex gap-3 justify-center pt-4">
                <Button variant="outline" onClick={restart}>
                  <RotateCcw className="w-4 h-4" />
                  {qt.restart}
                </Button>
                <Button asChild>
                  <Link to="/accueil-quiz">{qt.back}</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const q = sessionQuestions[current];

  if (!q) return null;

  return (
    <div className="min-h-screen bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <SEO 
          title="Kayn'in | Boussole Électorale 2026"
          description="Testez notre Boussole Électorale pour découvrir le parti politique qui correspond le mieux à vos valeurs."
          url="https://kaynin.ma/quiz"
      />
      <Navbar />
      <div className="pt-24 pb-16 container max-w-2xl">
        <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
          <Link to="/accueil-quiz">
            <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />
            {qt.back}
          </Link>
        </Button>
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
            <span>{qt.question} {current + 1} {qt.of} {sessionQuestions.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Card className="border-2 border-primary/20 shadow-[var(--shadow-elevated)]">
          <CardHeader>
            <CardTitle className={`font-display font-bold text-foreground leading-relaxed ${lang === 'ar' || lang === 'zgh' ? 'text-2xl md:text-3xl font-black' : 'text-xl'}`}>
              {q.question[lang]}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={answers[current] !== null ? String(answers[current]) : undefined}
              onValueChange={handleAnswer}
              className="space-y-3"
            >
              {q.options.map((opt, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    answers[current] === i
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-primary/30 hover:bg-muted/50"
                  }`}
                  onClick={() => handleAnswer(String(i))}
                >
                  <RadioGroupItem value={String(i)} id={`q${current}-o${i}`} />
                  <Label htmlFor={`q${current}-o${i}`} className={`cursor-pointer flex-1 ${lang === 'ar' || lang === 'zgh' ? 'text-base font-bold' : 'text-sm font-medium'}`}>
                    {opt.label[lang]}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {answers[current] === null && (
              <p className="text-xs text-muted-foreground mt-4 text-center">{qt.selectAnswer}</p>
            )}

            <div className="flex justify-between mt-6">
              <Button
                variant="outline"
                onClick={() => setCurrent(c => c - 1)}
                disabled={current === 0}
              >
                {isRtl ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
                {qt.prev}
              </Button>

              {current < sessionQuestions.length - 1 ? (
                <Button
                  onClick={() => setCurrent(c => c + 1)}
                  disabled={answers[current] === null}
                >
                  {qt.next}
                  {isRtl ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    recordPlay();
                    setShowResults(true);
                    addXp(50, "Quiz complété");
                    unlockBadge('quiz_master');
                  }}
                  disabled={answers[current] === null}
                >
                  {qt.seeResults}
                  <Trophy className="w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default QuizPage;
